import { Diff } from '../interfaces/diff';
import { SharedCommonUtility } from './common.utility';

export class SharedCollectionsUtility {
  public static difference<T>(left: T[], right: T[], keySelector?: (v: T) => string | number): T[] {
    let key: (a: T) => string | number = keySelector;
    if (typeof key !== 'function') {
      key = (a: T): string => (typeof a === 'object' ? JSON.stringify(a) : String(a));
    }
    const mapValues: Map<string | number, T> = new Map();

    right.forEach((value: T): any => mapValues.set(key(value), value));
    const removeFromMap = (value: T): void => {
      const valueKey: string | number = key(value);
      if (mapValues.has(valueKey)) {
        mapValues.delete(valueKey);
      }
    };
    left.forEach(removeFromMap);

    return Array.from(mapValues.values());
  }

  /**
   * Intersection between two arrays based on keySelector
   *
   * @param left - array to check the intersection. The values will be taken from this parameter
   * @param right - array to compare with
   * @param keySelector - unique key for object values. If not defined, uses string representation of the value
   */
  public static intersection<T>(left: T[], right: T[], keySelector?: (v: T) => string | number): T[] {
    let key: (a: T) => string | number = keySelector;
    if (typeof key !== 'function') {
      key = (a: T): string => (typeof a === 'object' ? JSON.stringify(a) : String(a));
    }

    const rightValues: Map<string | number, T> = new Map();
    const resultValues: Map<string | number, T> = new Map();

    right.forEach((value: T): any => rightValues.set(key(value), value));
    const removeFromMap = (value: T): void => {
      const valueKey: string | number = key(value);
      if (rightValues.has(valueKey)) {
        resultValues.set(valueKey, value);
      }
    };
    left.forEach(removeFromMap);

    return Array.from(resultValues.values());
  }

  public static objectDifference<T, K>(left: T, right: K, ...ignoreKeys: Array<keyof (T | K)>): Diff<T, K> {
    const diffKeys: Array<string> = SharedCollectionsUtility.difference(Object.keys(left), Object.keys(right));
    const result: Diff<T, K> = {} as any;
    diffKeys.forEach((key: string): void => (result[key] = right[key]));
    ignoreKeys.forEach((ignoredKey: keyof (T | K)) => (result[ignoredKey as string] = right[ignoredKey]));
    return result;
  }

  public static chunk<T>(array: T[], chunkSize: number): T[][] {
    const result: T[][] = [];

    for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize));
    }

    return result;
  }

  public static partition<T>(array: T[], filter: (value: T) => boolean): [T[], T[]] {
    const partitioned: [T[], T[]] = [[], []];
    array.forEach((obj: T) => partitioned[filter(obj) ? 0 : 1].push(obj));

    return partitioned;
  }

  /**
   * Chunks an array using preferred size and limiting number of chunks.
   * It uses the preferred chunk count for sizing the chunks up to the preferred chunk size.
   * Then it increases the chunk count, keeping the chunk size to the preferred size, up to an optional count limit,
   * and then it will increase the chunk size, fixing the chunk count.
   * If you want to limit the chunk size, then don't pass the chunk count limit, the size of the chunk will be limited
   * by the preferredChunkSize parameter.
   *
   * @param array - the array to chunk
   * @param preferredChunkSize - preferred chunk size if the preferred chunk count gives too big chunk size
   * @param preferredChunkCount - preferred chunk count, default = 1
   * @param maxChunkCount - number max of chunk count, chunk size will be adapted for the count not to exceed this value,
   * default = no limit, which means that preferredChunkSize is the maximum chunk size
   */
  public static adaptiveChunk<T>(
    array: T[],
    preferredChunkSize: number,
    preferredChunkCount: number = 1,
    maxChunkCount: number = Number.MAX_SAFE_INTEGER,
  ): T[][] {
    let chunkSize: number = Math.ceil(array.length / preferredChunkCount);
    if (chunkSize > preferredChunkSize) {
      chunkSize = preferredChunkSize;
      if (maxChunkCount < Number.MAX_SAFE_INTEGER && array.length > maxChunkCount * chunkSize) {
        chunkSize = Math.ceil(array.length / maxChunkCount);
      }
    }

    return SharedCollectionsUtility.chunk(array, chunkSize);
  }

  /**
   * Chunks an array of functions whose purpose is to generate the async calls, and executes them chunk by chunk. Use this to
   * avoid overloading database or APIs with tons of async calls at one time. In general, this method should be preferred over
   * Promise.all of unbounded arrays.
   *
   * This method uses the adaptiveChunk utility method for the chunking process. You can leverage the benefits of adaptiveChunk
   * method by adjusting the preferredChunkSize, preferredChunkCount and maxChunkCount parameters.
   *
   * @param {(() => Promise<T>)[]} asyncCallsGenerators List of generator functions that create the async calls to be chunked and processed.
   * @param {number} [preferredChunkSize=50] See {@link adaptiveChunk}
   * @param {number} [preferredChunkCount] See {@link adaptiveChunk}
   * @param {number} [maxChunkCount] See {@link adaptiveChunk}
   */
  public static async chunkedPromiseAll<T>(
    asyncCallsGenerators: (() => Promise<T>)[],
    preferredChunkSize: number = 50,
    preferredChunkCount?: number,
    maxChunkCount?: number,
  ): Promise<T[]> {
    const chunks: (() => Promise<T>)[][] = SharedCollectionsUtility.adaptiveChunk(
      asyncCallsGenerators,
      preferredChunkSize,
      preferredChunkCount,
      maxChunkCount,
    );
    const result: T[] = [];

    for (const chunkToProcess of chunks) {
      result.push(...(await Promise.all(chunkToProcess.map(async (f: () => Promise<T>): Promise<T> => f()))));
    }

    return result;
  }

  public static async processInChunks<C, R>(
    data: C[],
    chunkHandler: (chunk: C[]) => Promise<R>,
    preferredChunkSize: number = 50,
    preferredChunkCount?: number,
    maxChunkCount?: number,
  ): Promise<R> {
    const chunks: C[][] = SharedCollectionsUtility.adaptiveChunk(data, preferredChunkSize, preferredChunkCount, maxChunkCount);

    const queries: Promise<R>[] = chunks.map(async (chunk: C[]): Promise<R> => chunkHandler(chunk));

    const resultsFromQueries: R[] = await Promise.all(queries);

    return resultsFromQueries.flat() as R;
  }

  public static toThrottledIterable<T>(
    iterable: AsyncIterable<T>,
    options: { batchSize: number; throttlePerBatchInMs: number } = { batchSize: 100, throttlePerBatchInMs: 300 },
  ): AsyncIterable<T> {
    let yielded: number = 0;
    return {
      async *[Symbol.asyncIterator](): AsyncIterator<T> {
        for await (const item of iterable) {
          yield item;
          if (yielded++ > options.batchSize) {
            await SharedCommonUtility.sleep(options.throttlePerBatchInMs);
            yielded = 0;
          }
        }
      },
    };
  }
}
